<script setup>
import { ref, computed, getCurrentInstance } from "vue";
import api from "../api/api.js";
import { flashMessage } from "../functions.js";

// ── Pharmacy Account Profile ──────────────────────────────────────────────────
const profile = ref({
  customerNumber: "445321",
  pharmacyName:   "ABC Pharmacy",
  email:          "billing@abcpharmacy.com",
  phone:          "+1 (312) 752-8183",
  address:        "118 Main St S, Rochester, MN 55902",
});

// Dates are relative to today so the demo never looks stale ("today" = the bot conversation in the script)
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Matches the PDMI demo script (Scenes 2–4): what the bot handled before the chat reached the agent
const account = ref({
  accountStatus: "Active",
  intent:        "Claim Status, Prior Authorization",
});

// ── Claims ────────────────────────────────────────────────────────────────────
const claims = ref([
  {
    id: 1, claimNumber: "1197", drug: "Lisinopril 10mg", supply: "30-day supply",
    patientId: "883021", status: "Approved",
    detail: "Rejected Refill Too Soon (22 of 25 days) — early refill override applied, reprocessed",
    submitted: daysAgo(0), copayment: 10.00, current: true,
  },
  {
    id: 2, claimNumber: "1198", drug: "Atorvastatin 20mg", supply: "30-day supply",
    patientId: "883021", status: "Approved",
    detail: "Processed — covered under plan", submitted: daysAgo(1), copayment: 10.00, current: false,
  },
]);

// ── Open prior authorization (Scene 3) ───────────────────────────────────────
const priorAuth = ref({
  patientId:  "883021",
  drug:       "Humira 40mg",
  diagnosis:  "M06.9",
  tier:       "Tier 3 non-preferred specialty",
  rules:      "Quantity limit 2 pens / 28 days · Step therapy: methotrexate",
  alternative:"Enbrel (Tier 2) — no step therapy",
  status:     "Pending",
  missing:    "Chart notes showing trial and failure of methotrexate",
  opened:     daysAgo(0),
});

const remarks = ref([
  { id: 1,  date: daysAgo(0),  type: "Prior Auth", agent: "Virtual Agent", note: "ePA opened for patient #883021 — Humira 40mg, Dx M06.9. Tier 3 non-preferred specialty; quantity limit 2 pens/28 days; step therapy requires trial and failure of methotrexate. Pending: chart notes. Preferred alternative Enbrel (Tier 2)." },
  { id: 2,  date: daysAgo(0),  type: "Benefit",    agent: "Virtual Agent", note: "Coverage check for patient #883021 — active on plan. Atorvastatin on formulary as Tier 1 generic, no prior authorization required." },
  { id: 3,  date: daysAgo(0),  type: "Claim",      agent: "Virtual Agent", note: "Claim #1197 (lisinopril 10mg) rejected — Refill Too Soon: last fill 22 days ago, plan requires 25 days between fills. One-time early refill override applied at pharmacy request; reprocessed and approved, $10 copayment." },
  { id: 4,  date: daysAgo(0),  type: "Claim",      agent: "Virtual Agent", note: "Claim #1198 status check — atorvastatin 20mg, 30-day supply. Approved and processed, covered under the member's plan with a $10 copayment." },
]);

// ── Pharmacy reps (hardcoded for demo) ───────────────────────────────────────
const reps = ref([
  {
    id: 1,
    name:  "Tom Miller",
    email: "tom.miller@abcpharmacy.com",
    ani:   ["+1 (972) 301-1919"],
    secretQuestions: [
      { secretQuestion: "What is your store's NPI number?",            secretAnswer: "1487203941" },
      { secretQuestion: "What city was the pharmacy first licensed in?", secretAnswer: "Rochester" },
      { secretQuestion: "What is your pharmacy's NCPDP number?",        secretAnswer: "2394817" },
    ],
  },
  {
    id: 2,
    name:  "James Wilson",
    email: "james.wilson@abcpharmacy.com",
    ani:   ["+1 (312) 752-8183"],
    secretQuestions: [
      { secretQuestion: "What is your employee ID?",                  secretAnswer: "ABC-4417" },
      { secretQuestion: "What is the name of your supervising pharmacist?", secretAnswer: "Karen Doyle" },
      { secretQuestion: "What year did you join ABC Pharmacy?",       secretAnswer: "2019" },
    ],
  },
]);

// ── Fraud warnings (hardcoded for demo; T01 Caller ID and Customer Number messages) ─
const fraudWarnings = ref([
  "Global Warning: This caller ID has called 3 or more times in the last 48 hours.",
  "Global Warning: This Customer Number has been used 3 or more times in the last 48 hours.",
]);

const repSecretQModal = ref({ show: false, userName: "", questions: [] });

function openRepSecretQuestions(rep) {
  repSecretQModal.value.userName  = rep.name || "";
  repSecretQModal.value.questions = rep.secretQuestions || [];
  repSecretQModal.value.show      = true;
}

const newRemarkNote  = ref("");
const newRemarkType  = ref("Claim");
const remarkTypes    = ["Claim", "Expedited", "Prior Auth", "Benefit", "Pharmacy", "Rejection", "Complaint", "Note"];
const claimSearch    = ref("");

const fmt = (v) => "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2 });

const currentClaim = computed(() => claims.value.find(c => c.current) ?? null);

const filteredClaims = computed(() => {
  const q = claimSearch.value.trim().toLowerCase();
  if (!q) return claims.value;
  return claims.value.filter(c =>
    c.claimNumber.includes(q) || c.drug.toLowerCase().includes(q)
  );
});

const remarkTypeColor = (type) => ({
  Claim:        "#065f46",
  Expedited:    "#c2410c",
  "Prior Auth": "#6d28d9",
  Benefit:      "#1d6fa5",
  Pharmacy:     "#0e7490",
  Rejection:    "#b91c1c",
  Complaint:    "#b91c1c",
  Note:         "#374151",
}[type] || "#374151");

const claimStatusClass = (status) => ({
  Approved:      "pd-badge-filling",
  Paid:          "pd-badge-done",
  "PA Required": "pd-badge-pending",
  Rejected:      "pd-badge-rejected",
}[status] || "pd-badge-expired");

function saveRemark() {
  if (!newRemarkNote.value.trim()) return;
  remarks.value.unshift({
    id:    Date.now(),
    date:  new Date().toISOString().slice(0, 10),
    type:  newRemarkType.value,
    agent: "Current Agent",
    note:  newRemarkNote.value.trim(),
  });
  newRemarkNote.value = "";
}

const logocheck = require("@/assets/check.png");
const logoclose = require("@/assets/close.png");
const { proxy }  = getCurrentInstance();

function flash(message, color = "#4CAF50") {
  flashMessage(proxy.$flashMessage, color, "#ffffff", message);
}

function openCRM() {
  const imagePath = require(`@/assets/core_salesforce.png`);
  window.open(imagePath, "_blank", "width=1340,height=800");
}

// ── OTP (secondary verification) ─────────────────────────────────────────────
const OTP_PHONE             = "+13127528183";
const otpInput              = ref("");
const verificationAttempted = ref(false);
const isValid               = ref(false);

async function sendSMS() {
  try {
    const response = await api.post("/v2/SendOTP", {
      Company:      "5028",
      cellPhone:    OTP_PHONE,
      AuthToken:    "3301",
      memberNumber: "445321",
    }, {
      headers: { token: "2y10D08eZgJLx0C9rGvQKfwFBuCPvqDdmdowpnczUKwYWU3B0VoVt5BiC" }
    });
    flash(response.data.Response || "OTP sent");
  } catch {
    flash("Failed to send OTP", "#FF0000");
  }
}

async function verifyOTP() {
  try {
    const response = await api.post("/v2/CheckOTP", {
      Company:   "5028",
      cellPhone: OTP_PHONE,
      OTP:       otpInput.value,
      AuthToken: "3301",
    }, {
      headers: { token: "2y10D08eZgJLx0C9rGvQKfwFBuCPvqDdmdowpnczUKwYWU3B0VoVt5BiC" }
    });
    verificationAttempted.value = true;
    isValid.value = response.data.Response === "OTP Validated";
    flash(response.data.Response, isValid.value ? "#4CAF50" : "#FF0000");
  } catch {
    flash("Failed to verify OTP", "#FF0000");
  }
}
</script>

<template>
  <div class="pd-page">
    <div class="pd-card">

      <!-- ── Card header ──────────────────────────────────────────────────── -->
      <div class="pd-topbar">
        <h2 class="pd-screen-id">Customer Information</h2>
        <div class="pd-topbar-right">
          <button class="pd-goto-btn" @click="openCRM">Go to CRM</button>
          <span class="pd-screen-code">C01</span>
        </div>
      </div>

      <!-- ── Subheader strip ───────────────────────────────────────────────── -->
      <div class="pd-subheader">
        <div>
          <div class="pd-sub-row">
            <span class="pd-sub-lbl">Customer #:</span>
            <span class="pd-sub-val">{{ profile.customerNumber }}</span>
          </div>
          <div class="pd-sub-row">
            <span class="pd-sub-lbl">Pharmacy:</span>
            <span class="pd-sub-val pd-sub-val--name">{{ profile.pharmacyName }}</span>
          </div>
          <div class="pd-sub-row">
            <span class="pd-sub-lbl">Email:</span>
            <span class="pd-sub-val">{{ profile.email }}</span>
          </div>
          <div class="pd-sub-row">
            <span class="pd-sub-lbl">Phone:</span>
            <span class="pd-sub-val">{{ profile.phone }}</span>
          </div>
          <div class="pd-sub-row">
            <span class="pd-sub-lbl">Address:</span>
            <span class="pd-sub-val">{{ profile.address }}</span>
          </div>
        </div>
        <div>
          <div class="pd-sub-row">
            <span class="pd-sub-lbl">Account:</span>
            <span class="pd-sub-val pd-sub-val--status">{{ account.accountStatus }}</span>
          </div>
          <div class="pd-sub-row">
            <span class="pd-sub-lbl">Intent:</span>
            <span class="pd-sub-val" style="font-weight:600;">{{ account.intent }}</span>
          </div>
        </div>

        <!-- 2FA — top right -->
        <div class="pd-subheader-2fa">
          <fieldset class="pd-fieldset" style="margin-top:0;">
            <legend class="pd-legend">2FA</legend>
            <div style="display:flex; align-items:center; gap:6px;">
              <input class="pd-search-input" :value="profile.phone" disabled style="background:#f3f4f6; color:#374151; margin:0; flex:1;" />
              <button class="pd-submit-btn" @click="sendSMS" style="margin:0; white-space:nowrap;">Send SMS</button>
            </div>
            <div style="display:flex; align-items:center; gap:6px;">
              <input class="pd-search-input" v-model="otpInput" placeholder="OTP" style="margin:0; width:80px; flex:none;" />
              <img v-if="verificationAttempted && isValid"  :src="logocheck" alt="valid"   style="width:18px;height:18px;flex-shrink:0;" />
              <img v-if="verificationAttempted && !isValid" :src="logoclose" alt="invalid" style="width:18px;height:18px;flex-shrink:0;" />
              <button class="pd-submit-btn" @click="verifyOTP" style="margin:0;">Validate</button>
            </div>
          </fieldset>
        </div>
      </div>

      <!-- ── 3-column body ──────────────────────────────────────────────────── -->
      <div class="pd-centerdiv">

        <!-- ═══ COL 1: Pharmacy Identity + 2FA ══════════════════════════════ -->
        <div class="pd-col">

          <!-- Search -->
          <h4>Search for</h4>
          <div class="pd-search-group">
            <input class="pd-search-input" type="text" placeholder="Claim number, Rx number, or case ID" />
            <input class="pd-search-input" type="text" placeholder="Email Address" />
          </div>
          <div style="margin: 8px 0 18px;">
            <button class="pd-submit-btn">Find</button>
            <button class="pd-cancel-btn">Cancel</button>
          </div>

          <!-- Pharmacy Identity -->
          <h4>Pharmacy Identity</h4>

          <!-- ID strip -->
          <div class="pd-profile-ids">
            <div class="pd-profile-id-item">
              <span class="pd-sub-lbl">Customer #</span>
              <span class="pd-profile-id-val">{{ profile.customerNumber }}</span>
            </div>
            <div class="pd-profile-id-item">
              <span class="pd-sub-lbl">Claims</span>
              <span class="pd-profile-id-val">{{ claims.length }}</span>
            </div>
          </div>

          <!-- Pharmacy Representatives -->
          <h4 style="margin-top:20px;">Pharmacy Representatives</h4>
          <table class="pd-data-table">
            <thead>
              <tr>
                <th style="width:26%;">Name</th>
                <th style="width:34%;">Email</th>
                <th style="width:26%;">Phone</th>
                <th style="text-align:center; width:14%;">Auth</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="rep in reps" :key="rep.id">
                <td style="font-weight:600;">{{ rep.name }}</td>
                <td style="overflow-wrap:anywhere; font-size:12px;">{{ rep.email }}</td>
                <td style="font-size:12px;">
                  <div v-for="(phone, pi) in (rep.ani || [])" :key="pi">{{ phone }}</div>
                </td>
                <td style="text-align:center;">
                  <button
                    v-if="rep.secretQuestions && rep.secretQuestions.length"
                    @click="openRepSecretQuestions(rep)"
                    title="View Security Questions"
                    style="background:none; border:none; cursor:pointer; padding:0; line-height:0;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 64 64">
                      <rect x="10" y="4" width="36" height="48" rx="5" ry="5" fill="#2d3f55"/>
                      <rect x="14" y="9" width="28" height="36" rx="2" ry="2" fill="#f0f4f8"/>
                      <circle cx="28" cy="52" r="2.2" fill="#f0f4f8"/>
                      <text x="28" y="33" text-anchor="middle" font-size="22" font-weight="bold" fill="#9aafc5" font-family="serif">?</text>
                      <circle cx="46" cy="44" r="11" fill="#7a8fa6"/>
                      <polyline points="40,44 44,48 52,40" stroke="#fff" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                    </svg>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Fraud Warnings -->
          <h4 style="margin-top:20px;">Fraud Warnings</h4>
          <div class="pd-warnings">
            <div v-for="(msg, i) in fraudWarnings" :key="i" class="pd-warning">{{ msg }}</div>
            <div v-if="!fraudWarnings.length" style="font-style:italic; color:#6b7280;">No warnings.</div>
          </div>

        </div>

        <!-- ═══ COL 2: Call Remarks / Case History ═══════════════════════════ -->
        <div class="pd-col">

          <h4>Call Remarks &amp; Case History</h4>
          <div style="display:flex; gap:8px; margin-bottom:10px; align-items:flex-start;">
            <div class="pd-remark-type-wrap">
              <select class="pd-remark-type-select" v-model="newRemarkType">
                <option v-for="t in remarkTypes" :key="t">{{ t }}</option>
              </select>
              <span class="pd-remark-type-chevron">▾</span>
            </div>
            <textarea class="pd-textarea" v-model="newRemarkNote"
              placeholder="Add a remark..." rows="2"
              style="flex:1; margin:0;"></textarea>
          </div>
          <div style="margin-bottom:14px;">
            <button class="pd-submit-btn" @click="saveRemark">Save</button>
            <button class="pd-cancel-btn" @click="newRemarkNote=''">Cancel</button>
          </div>

          <table class="pd-data-table">
            <thead>
              <tr>
                <th style="width:20%;">Type</th>
                <th style="width:20%;">Date</th>
                <th style="width:18%;">Agent</th>
                <th style="width:42%;">Note</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in remarks" :key="r.id">
                <td>
                  <span class="pd-type-dot" :style="{ background: remarkTypeColor(r.type) }"></span>
                  {{ r.type }}
                </td>
                <td style="color:#555;">{{ r.date }}</td>
                <td style="color:#555;">{{ r.agent }}</td>
                <td>{{ r.note }}</td>
              </tr>
            </tbody>
          </table>

        </div>

        <!-- ═══ COL 3: Current Claim + Prior Authorization + Claim History ═══ -->
        <div class="pd-col">

          <!-- Current Claim callout -->
          <h4>Current Claim</h4>
          <div v-if="currentClaim" class="pd-current-order">
            <div class="pd-current-order-head">
              <span class="pd-current-order-num">Claim #{{ currentClaim.claimNumber }}</span>
              <span :class="claimStatusClass(currentClaim.status)">{{ currentClaim.status }}</span>
            </div>
            <div class="pd-current-order-drug">{{ currentClaim.drug }}</div>
            <div class="pd-current-order-meta">{{ currentClaim.supply }} &middot; Patient #{{ currentClaim.patientId }}</div>
            <div class="pd-current-order-ready">
              {{ currentClaim.detail }}
            </div>
            <div class="pd-current-order-meta">
              <strong>Member copayment:</strong> {{ fmt(currentClaim.copayment) }}
            </div>
          </div>
          <p v-else class="pd-hint">No claim in progress.</p>

          <!-- Open prior authorization (Scene 3) -->
          <h4 style="margin-top:20px;">Prior Authorization</h4>
          <div class="pd-current-order pd-pa">
            <div class="pd-current-order-head">
              <span class="pd-current-order-num">ePA &middot; Patient #{{ priorAuth.patientId }}</span>
              <span class="pd-badge-pending">{{ priorAuth.status }}</span>
            </div>
            <div class="pd-current-order-drug">{{ priorAuth.drug }} &middot; Dx {{ priorAuth.diagnosis }}</div>
            <div class="pd-current-order-meta">{{ priorAuth.tier }}</div>
            <div class="pd-current-order-meta">{{ priorAuth.rules }}</div>
            <div class="pd-pa-missing">
              <strong>Missing:</strong> {{ priorAuth.missing }}
            </div>
            <div class="pd-current-order-meta">
              <strong>Preferred alternative:</strong> {{ priorAuth.alternative }}
            </div>
          </div>

          <!-- Claim History -->
          <h4 style="margin-top:20px;">Submitted Claims</h4>
          <input class="pd-search-input" v-model="claimSearch"
            placeholder="Filter by claim # or drug name" style="margin-bottom:8px;" />
          <table class="pd-data-table">
            <thead>
              <tr>
                <th style="width:16%;">Claim #</th>
                <th style="width:38%;">Medication</th>
                <th style="width:30%;">Status</th>
                <th style="width:16%; text-align:right;">Copayment</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in filteredClaims" :key="c.id" :class="{ 'pd-row-current': c.current }">
                <td style="font-family:monospace; font-weight:600;">{{ c.claimNumber }}</td>
                <td>
                  <div style="font-weight:600; font-size:13px;">{{ c.drug }}</div>
                  <div style="font-size:11px; color:#888;">{{ c.supply }} &middot; Patient #{{ c.patientId }}</div>
                </td>
                <td>
                  <span :class="claimStatusClass(c.status)">{{ c.status }}</span>
                  <div style="font-size:11px; color:#888; margin-top:2px;">{{ c.detail }}</div>
                </td>
                <td style="text-align:right; white-space:nowrap;">{{ fmt(c.copayment) }}</td>
              </tr>
              <tr v-if="!filteredClaims.length">
                <td colspan="4" style="color:#aaa; font-style:italic;">No claims match that filter.</td>
              </tr>
            </tbody>
          </table>

        </div>

      </div>
    </div><!-- /pd-card -->

    <!-- Rep Security Questions Modal -->
    <div v-if="repSecretQModal.show"
      style="position:fixed; top:0; left:0; width:100%; height:100%; z-index:9999; display:flex; align-items:center; justify-content:center;">
      <div @click="repSecretQModal.show = false"
        style="position:absolute; top:0; left:0; width:100%; height:100%; background:rgba(0,0,0,0.4);"></div>
      <div style="position:relative; background:#fff; border-radius:8px; overflow:hidden; width:480px; max-width:calc(100vw - 32px); z-index:1; box-shadow:0 8px 32px rgba(0,0,0,0.24);">
        <!-- Modal header -->
        <div style="background:#0e5071; padding:14px 20px; display:flex; align-items:center; justify-content:space-between;">
          <h4 style="margin:0; font-size:15px; color:#fff; font-weight:600;">Security Questions — {{ repSecretQModal.userName }}</h4>
          <button @click="repSecretQModal.show = false"
            style="background:none; border:none; cursor:pointer; font-size:20px; color:#fff; line-height:1; opacity:0.8;">×</button>
        </div>
        <!-- Modal body -->
        <div style="padding:16px 20px;">
          <div v-for="(sq, i) in repSecretQModal.questions" :key="i"
            style="padding:10px 12px; margin-bottom:8px; background:#f8fafc; border-left:3px solid #0e5071; border-radius:0 4px 4px 0;">
            <div style="font-size:13px; color:#6b7280; margin-bottom:3px;">Q: {{ sq.secretQuestion }}</div>
            <div style="font-size:14px; font-weight:600; color:#0e5071;">A: {{ sq.secretAnswer }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pd-page {
  font-family: "Open Sans", "Segoe UI", sans-serif;
  font-size: 15px;
  background: #e8e8e8;
  min-height: 100vh;
  padding: 20px;
  color: #222;
  box-sizing: border-box;
}

.pd-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.12);
  overflow: hidden;
}

/* ── Header ─────────────────────────────────────────────────────────────────── */
.pd-topbar {
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e8e8e8;
}
.pd-screen-id   { font-size: 18px; font-weight: 600; color: #222; margin: 0; }
.pd-screen-code { font-size: 18px; font-weight: 600; color: #888; }
.pd-topbar-right { display: flex; align-items: center; gap: 14px; }
.pd-goto-btn {
  background: #0e5071;
  color: #fff;
  border: none;
  border-radius: 50px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.02em;
}
.pd-goto-btn:hover { background: #0c4160; }

/* ── Subheader ───────────────────────────────────────────────────────────────── */
.pd-subheader {
  border-bottom: 1px solid #e8e8e8;
  padding: 10px 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px 60px;
  align-items: flex-start;
}
.pd-subheader > div { min-width: 0; }
/* Pushes the 2FA block to the far right of the subheader */
.pd-subheader-2fa {
  margin-left: auto;
  flex-shrink: 0;
  width: 300px;
  max-width: 100%;
}
.pd-sub-row { display: flex; gap: 6px; padding: 1px 0; font-size: 14px; }
.pd-sub-lbl { color: #555; min-width: 90px; flex-shrink: 0; }
.pd-sub-val { color: #222; min-width: 0; overflow-wrap: anywhere; }
.pd-sub-val--name   { font-weight: 600; }
.pd-sub-val--status { font-weight: 700; color: #0e5071; }

/* ── 3-column layout ─────────────────────────────────────────────────────────── */
.pd-centerdiv {
  display: flex;
  gap: 0;
  padding: 0;
  align-items: stretch;
}
.pd-col {
  flex: 1;
  padding: 16px 20px;
  min-width: 0;
  border-right: 1px solid #e8e8e8;
}
.pd-col:last-child { border-right: none; }
.pd-col h4 {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 10px;
  color: #222;
}

/* ── Responsive: driven by the card's own width, not the browser viewport, so it
      behaves correctly inside a narrow Genesys iframe ───────────────────────── */
.pd-card { container-type: inline-size; }

/* Medium — 3 columns can't hold their content; drop to 2 with the claims column full width */
@container (max-width: 1100px) {
  .pd-centerdiv { flex-wrap: wrap; }
  .pd-col { flex: 1 1 50%; border-bottom: 1px solid #e8e8e8; }
  .pd-col:nth-child(2) { border-right: none; }
  .pd-col:nth-child(3) { flex-basis: 100%; border-bottom: none; }
  .pd-subheader-2fa { margin-left: 0; }
}

/* Narrow — single column stack */
@container (max-width: 720px) {
  .pd-subheader { gap: 12px 24px; padding: 10px 14px; }
  .pd-subheader > div { flex: 1 1 100%; }
  .pd-subheader-2fa { width: 100%; }
  .pd-col { flex-basis: 100% !important; border-right: none; padding: 14px; }
  .pd-col:last-child { border-bottom: none; }
  .pd-sub-lbl { min-width: 80px; }
  .pd-topbar { padding: 12px 14px; }
  .pd-data-table th, .pd-data-table td { padding: 6px; }
}

/* Trim the page gutter when there isn't room for it */
@media (max-width: 900px) {
  .pd-page { padding: 10px; }
}
@media (max-width: 600px) {
  .pd-page { padding: 0; }
  .pd-card { border-radius: 0; }
}

/* Fallback for engines without container-query support — keeps the stack behavior
   usable when the iframe is narrow because the window itself is narrow */
@supports not (container-type: inline-size) {
  @media (max-width: 1100px) {
    .pd-centerdiv { flex-wrap: wrap; }
    .pd-col { flex: 1 1 50%; border-bottom: 1px solid #e8e8e8; }
    .pd-col:nth-child(3) { flex-basis: 100%; border-bottom: none; }
  }
  @media (max-width: 720px) {
    .pd-col { flex-basis: 100% !important; border-right: none; }
    .pd-subheader > div { flex: 1 1 100%; }
    .pd-subheader-2fa { width: 100%; margin-left: 0; }
  }
}

/* ── ID strip ────────────────────────────────────────────────────────────────── */
.pd-profile-ids {
  display: flex;
  gap: 0;
  background: #f5f7f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}
.pd-profile-id-item {
  flex: 1;
  padding: 7px 10px;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.pd-profile-id-item:last-child { border-right: none; }
.pd-profile-id-val {
  font-size: 13px;
  font-weight: 600;
  color: #0e5071;
  font-family: monospace;
}

/* ── Search ──────────────────────────────────────────────────────────────────── */
.pd-search-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 0; }
.pd-search-input {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 5px 8px;
  font-size: 14px;
  font-family: inherit;
  box-sizing: border-box;
}
.pd-search-input:focus { outline: none; border-color: #0e5071; }

/* ── Fieldsets ───────────────────────────────────────────────────────────────── */
.pd-fieldset {
  margin-top: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pd-legend { font-weight: 600; font-size: 13px; color: #374151; padding: 0 6px; }

/* ── Info table ──────────────────────────────────────────────────────────────── */
.pd-info-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.pd-info-table td { padding: 4px 6px; vertical-align: top; }
.pd-td-lbl { color: #555; width: 120px; white-space: nowrap; }

/* ── Data table ──────────────────────────────────────────────────────────────── */
.pd-data-table { width: 100%; border-collapse: collapse; font-size: 13px; table-layout: fixed; }

/* Fraud warnings (same look as M01 / NineStar) */
.pd-warnings { border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px; font-size: 13px; }
.pd-warning {
  padding: 6px 8px;
  margin-bottom: 6px;
  background: #fff5f5;
  border-left: 3px solid #e74c3c;
  border-radius: 0 3px 3px 0;
  color: #c0392b;
  line-height: 1.4;
}
.pd-warning:last-child { margin-bottom: 0; }
.pd-data-table thead tr { background: #0e5071; }
.pd-data-table th {
  padding: 7px 10px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: #fff !important;
  white-space: nowrap;
}
.pd-data-table tbody tr:nth-child(odd)  { background: #fff; }
.pd-data-table tbody tr:nth-child(even) { background: #f5f7f9; }
.pd-data-table tbody tr { border-bottom: 1px solid #e8e8e8; }
.pd-data-table tbody tr:last-child { border-bottom: none; }
.pd-data-table td { padding: 6px 10px; vertical-align: top; color: #333; overflow-wrap: anywhere; }
.pd-data-table tfoot td { background: #fff; border-top: 2px solid #ddd; padding-top: 8px; }
.pd-data-table tbody tr.pd-row-current { background: #eff6ff; }

/* ── Current order callout ───────────────────────────────────────────────────── */
.pd-current-order {
  overflow-wrap: anywhere;
  border: 1px solid #bfdbfe;
  border-left: 4px solid #0e5071;
  background: #f0f7ff;
  border-radius: 4px;
  padding: 10px 12px;
}
.pd-current-order-head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.pd-current-order-num  { font-family: monospace; font-weight: 700; font-size: 14px; color: #0e5071; }
.pd-current-order-drug { font-weight: 600; font-size: 14px; margin-bottom: 2px; }
.pd-current-order-meta { font-size: 12px; color: #555; margin-top: 2px; }
.pd-current-order-ready{ font-size: 13px; color: #065f46; margin-top: 6px; }

/* ── Prior authorization callout ─────────────────────────────────────────────── */
.pd-pa { border-color: #ddd6fe; border-left-color: #6d28d9; background: #faf5ff; }
.pd-pa .pd-current-order-num { color: #6d28d9; }
.pd-pa-missing { font-size: 13px; color: #92400e; margin-top: 6px; }

/* ── Textarea ────────────────────────────────────────────────────────────────── */
.pd-textarea {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 6px 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  color: #222;
  box-sizing: border-box;
}

/* ── Remark type dropdown ────────────────────────────────────────────────────── */
.pd-remark-type-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.pd-remark-type-select {
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid #b0b0b0;
  border-radius: 4px;
  padding: 5px 28px 5px 10px;
  font-size: 13px;
  font-family: inherit;
  font-weight: 600;
  background: #fff;
  color: #454a4d;
  cursor: pointer;
  min-width: 110px;
}
.pd-remark-type-select:focus { outline: none; border-color: #0e5071; }
.pd-remark-type-chevron {
  position: absolute;
  right: 8px;
  pointer-events: none;
  font-size: 11px;
  color: #888;
}

/* ── Buttons ─────────────────────────────────────────────────────────────────── */
.pd-submit-btn, .pd-cancel-btn {
  border: none;
  border-radius: 20px;
  padding: 5px 18px;
  font-size: 13px;
  cursor: pointer;
  margin-right: 6px;
}
.pd-submit-btn { background: #0e5071; color: #fff; }
.pd-submit-btn:hover { background: #0c4160; }
.pd-cancel-btn { background: #b0b0b0; color: #fff; }
.pd-cancel-btn:hover { background: #999; }

/* ── Type dot ────────────────────────────────────────────────────────────────── */
.pd-type-dot {
  display: inline-block;
  width: 8px; height: 8px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}

/* ── Hint ────────────────────────────────────────────────────────────────────── */
.pd-hint { font-size: 12px; color: #888; margin: -6px 0 8px; }

/* ── Status badges ───────────────────────────────────────────────────────────── */
.pd-badge-active,
.pd-badge-filling,
.pd-badge-pending,
.pd-badge-done,
.pd-badge-expired,
.pd-badge-rejected {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  max-width: 100%;
}
.pd-badge-active  { background: #d1fae5; color: #065f46; }
.pd-badge-filling { background: #dbeafe; color: #1e40af; }
.pd-badge-pending { background: #ede9fe; color: #5b21b6; }
.pd-badge-done    { background: #f0fdf4; color: #15803d; }
.pd-badge-expired  { background: #f3f4f6; color: #6b7280; }
.pd-badge-rejected { background: #fee2e2; color: #991b1b; }
</style>
